import React, { useState, useEffect } from 'react';
import {
  Plus,
  Lock,
  LockOpen,
  Calendar,
  IndianRupee,
  Target,
  Users,
  Percent,
  Save,
  Trash2,
  Loader2,
  Info,
  Building2,
  Award
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import ConfirmDialog from '../../components/ui/confirm-dialog';
import { useConfirm } from '../../components/ui/use-confirm';
// (deduped imports)

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Switch } from '../../components/ui/switch';
import {
  Alert,
  AlertDescription,
} from '../../components/ui/alert';
import { incentivesAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import { usePrivacy } from '../../contexts/PrivacyContext';

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonth = (monthStr) => {
  if (!monthStr) return '-';
  const [year, month] = monthStr.split('-');
  const date = new Date(year, parseInt(month) - 1);
  return date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

const IncentiveConfigPage = () => {
  const { formatMoney } = usePrivacy();
  const formatCurrency = formatMoney; // Alias for compatibility
  const { confirm, dialogProps } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('company-sqd');
  
  // Company SQD Slabs
  const [companySqdSlabs, setCompanySqdSlabs] = useState([]);
  const [isAddCompanySqdOpen, setIsAddCompanySqdOpen] = useState(false);
  const [newCompanySqdSlab, setNewCompanySqdSlab] = useState({
    effective_month: getCurrentMonth(),
    min_sqd: 1,
    max_sqd: '',
    amount_per_sqd: 1000
  });
  
  // Company Conversion Config
  const [companyConversionConfigs, setCompanyConversionConfigs] = useState([]);
  const [isAddConversionOpen, setIsAddConversionOpen] = useState(false);
  const [newConversionConfig, setNewConversionConfig] = useState({
    effective_month: getCurrentMonth(),
    first_year_months: '1,3,5',
    renewal_months: '1,2',
    include_one_time: true
  });
  
  // Team SQD Slabs
  const [teamSqdSlabs, setTeamSqdSlabs] = useState([]);
  const [isAddTeamSqdSlabOpen, setIsAddTeamSqdSlabOpen] = useState(false);
  const [newTeamSqdSlab, setNewTeamSqdSlab] = useState({
    effective_month: getCurrentMonth(),
    min_sqd: 1,
    max_sqd: '',
    amount_per_sqd: 500
  });
  
  // Team Conversion Incentive
  const [teamConversionConfigs, setTeamConversionConfigs] = useState([]);
  const [isAddTeamConversionOpen, setIsAddTeamConversionOpen] = useState(false);
  const [newTeamConversionConfig, setNewTeamConversionConfig] = useState({
    effective_month: getCurrentMonth(),
    first_month_plan_percentage: 10,
    setup_fee_percentage: 10,
    renewal_first_month_percentage: 5
  });

  useEffect(() => {
    fetchAllConfigs();
  }, []);

  const fetchAllConfigs = async () => {
    try {
      setLoading(true);
      const [sqdRes, convRes, teamSqdSlabRes, teamConvRes] = await Promise.all([
        incentivesAPI.listSQDSlabs().catch(() => ({ data: [] })),
        incentivesAPI.listConversionConfigs().catch(() => ({ data: [] })),
        incentivesAPI.listTeamSQDSlabs().catch(() => ({ data: [] })),
        incentivesAPI.listTeamConfigs().catch(() => ({ data: [] }))
      ]);
      setCompanySqdSlabs(sqdRes.data || []);
      setCompanyConversionConfigs(convRes.data || []);
      setTeamSqdSlabs(teamSqdSlabRes.data || []);
      setTeamConversionConfigs(teamConvRes.data || []);
    } catch (error) {
      console.error('Failed to fetch configs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Company SQD Slab handlers
  const handleAddCompanySqdSlab = async () => {
    try {
      await incentivesAPI.createSQDSlab({
        ...newCompanySqdSlab,
        max_sqd: newCompanySqdSlab.max_sqd || null
      });
      setIsAddCompanySqdOpen(false);
      setNewCompanySqdSlab({ effective_month: getCurrentMonth(), min_sqd: 1, max_sqd: '', amount_per_sqd: 1000 });
      fetchAllConfigs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to add slab',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleDeleteCompanySqdSlab = async (id) => {
    const ok = await confirm({
      title: 'Delete SQD slab?',
      description: 'This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true
    });
    if (!ok) return;
    try {
      await incentivesAPI.deleteSQDSlab(id);
      fetchAllConfigs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleLockCompanySqdSlab = async (id) => {
    try {
      await incentivesAPI.lockSQDSlab(id);
      fetchAllConfigs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to lock',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  // Team SQD Slab handlers
  const handleAddTeamSqdSlab = async () => {
    try {
      await incentivesAPI.createTeamSQDSlab({
        ...newTeamSqdSlab,
        max_sqd: newTeamSqdSlab.max_sqd || null
      });
      setIsAddTeamSqdSlabOpen(false);
      setNewTeamSqdSlab({ effective_month: getCurrentMonth(), min_sqd: 1, max_sqd: '', amount_per_sqd: 500 });
      fetchAllConfigs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to add slab',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleDeleteTeamSqdSlab = async (id) => {
    const ok = await confirm({
      title: 'Delete Team SQD slab?',
      description: 'This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true
    });
    if (!ok) return;
    try {
      await incentivesAPI.deleteTeamSQDSlab(id);
      fetchAllConfigs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  // Company Conversion Config handlers
  const handleAddConversionConfig = async () => {
    try {
      const firstYearMonths = newConversionConfig.first_year_months.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
      const renewalMonths = newConversionConfig.renewal_months.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
      
      await incentivesAPI.createConversionConfig({
        effective_month: newConversionConfig.effective_month,
        first_year_claimable_months: firstYearMonths,
        renewal_year_claimable_months: renewalMonths,
        include_one_time_charge: newConversionConfig.include_one_time
      });
      setIsAddConversionOpen(false);
      setNewConversionConfig({ effective_month: getCurrentMonth(), first_year_months: '1,3,5', renewal_months: '1,2', include_one_time: true });
      fetchAllConfigs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to add config',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleDeleteConversionConfig = async (id) => {
    const ok = await confirm({
      title: 'Delete Conversion config?',
      description: 'This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true
    });
    if (!ok) return;
    try {
      await incentivesAPI.deleteConversionConfig(id);
      fetchAllConfigs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleLockConversionConfig = async (id) => {
    try {
      await incentivesAPI.lockConversionConfig(id);
      fetchAllConfigs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to lock',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  // Team Conversion Config handlers
  const handleAddTeamConversionConfig = async () => {
    try {
      await incentivesAPI.createTeamConfig({
        effective_month: newTeamConversionConfig.effective_month,
        first_month_plan_percentage: newTeamConversionConfig.first_month_plan_percentage,
        setup_fee_percentage: newTeamConversionConfig.setup_fee_percentage,
        renewal_first_month_percentage: newTeamConversionConfig.renewal_first_month_percentage
      });
      setIsAddTeamConversionOpen(false);
      setNewTeamConversionConfig({ effective_month: getCurrentMonth(), first_month_plan_percentage: 10, setup_fee_percentage: 10, renewal_first_month_percentage: 5 });
      fetchAllConfigs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to add config',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleDeleteTeamConversionConfig = async (id) => {
    const ok = await confirm({
      title: 'Delete Team Conversion config?',
      description: 'This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true
    });
    if (!ok) return;
    try {
      await incentivesAPI.deleteTeamConfig(id);
      fetchAllConfigs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleLockTeamConversionConfig = async (id) => {
    try {
      await incentivesAPI.lockTeamConfig(id);
      fetchAllConfigs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to lock',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog
        open={dialogProps.open}
        onOpenChange={dialogProps.onOpenChange}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmText={dialogProps.confirmText}
        cancelText={dialogProps.cancelText}
        destructive={dialogProps.destructive}
        onConfirm={dialogProps.onConfirm}
        onCancel={dialogProps.onCancel}
      />

      <ConfirmDialog
        open={dialogProps.open}
        onOpenChange={dialogProps.onOpenChange}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmText={dialogProps.confirmText}
        cancelText={dialogProps.cancelText}
        destructive={dialogProps.destructive}
        onConfirm={dialogProps.onConfirm}
        onCancel={dialogProps.onCancel}
      />

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Incentive Configuration</h1>
        <p className="text-slate-500 dark:text-slate-400">Configure company receivables and team payables</p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 border-blue-200 dark:border-blue-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <Building2 className="w-10 h-10 text-blue-600 dark:text-blue-400" />
              <div>
                <h3 className="font-semibold text-blue-800 dark:text-blue-300">Company Receivables</h3>
                <p className="text-sm text-blue-600 dark:text-blue-400">What we earn from SaaS company</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-800/30 border-emerald-200 dark:border-emerald-800">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <Users className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
              <div>
                <h3 className="font-semibold text-emerald-800 dark:text-emerald-300">Team Payables</h3>
                <p className="text-sm text-emerald-600 dark:text-emerald-400">What we pay to employees</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="company-sqd">Company SQD</TabsTrigger>
          <TabsTrigger value="company-conversion">Company Conversion</TabsTrigger>
          <TabsTrigger value="team-sqd">Team SQD</TabsTrigger>
          <TabsTrigger value="team-conversion">Team Conversion</TabsTrigger>
        </TabsList>

        {/* Company SQD Slabs */}
        <TabsContent value="company-sqd" className="mt-6">
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  SQD Revenue Slabs
                </CardTitle>
                <CardDescription>Amount we receive per SQD from SaaS company (slab-based)</CardDescription>
              </div>
              <Button onClick={() => setIsAddCompanySqdOpen(true)}>
                <Plus className="w-4 h-4 mr-2" /> Add Slab
              </Button>
            </CardHeader>
            <CardContent>
              <Alert className="mb-4">
                <Info className="w-4 h-4" />
                <AlertDescription>
                  Non-cumulative: The slab where total SQD count falls applies to ALL SQDs.
                </AlertDescription>
              </Alert>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[150px]">W.E.F.</TableHead>
                    <TableHead className="w-[120px]">SQD Range</TableHead>
                    <TableHead className="w-[150px]">Amount/SQD</TableHead>
                    <TableHead className="w-[100px]">Status</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companySqdSlabs.length > 0 ? companySqdSlabs.map(slab => (
                    <TableRow key={slab.id}>
                      <TableCell>{formatMonth(slab.effective_month)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{slab.min_sqd} - {slab.max_sqd || '∞'}</Badge>
                      </TableCell>
                      <TableCell className="font-bold text-blue-600">{formatCurrency(slab.amount_per_sqd)}</TableCell>
                      <TableCell>
                        {slab.is_locked ? (
                          <Badge className="bg-amber-100 text-amber-700"><Lock className="w-3 h-3 mr-1" />Locked</Badge>
                        ) : (
                          <Badge variant="outline" className="text-emerald-600"><LockOpen className="w-3 h-3 mr-1" />Active</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {!slab.is_locked && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleLockCompanySqdSlab(slab.id)} title="Lock">
                                <Lock className="w-4 h-4 text-amber-500" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteCompanySqdSlab(slab.id)} title="Delete">
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        No SQD slabs configured. Add your first slab.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Company Conversion */}
        <TabsContent value="company-conversion" className="mt-6">
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Conversion Revenue Config
                </CardTitle>
                <CardDescription>
                  Which months of plan amount we claim from SaaS company on conversion
                </CardDescription>
              </div>
              <Button onClick={() => setIsAddConversionOpen(true)}>
                <Plus className="w-4 h-4 mr-2" /> Add Config
              </Button>
            </CardHeader>
            <CardContent>
              <Alert className="mb-4">
                <Info className="w-4 h-4" />
                <AlertDescription>
                  Example: First year we claim months 1, 3, 5. Renewal years we claim months 1, 2. Plus 100% of one-time setup charge.
                </AlertDescription>
              </Alert>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[130px]">W.E.F.</TableHead>
                    <TableHead className="w-[150px]">1st Year Months</TableHead>
                    <TableHead className="w-[150px]">Renewal Months</TableHead>
                    <TableHead className="w-[120px]">One-time</TableHead>
                    <TableHead className="w-[100px]">Status</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companyConversionConfigs.length > 0 ? companyConversionConfigs.map(cfg => (
                    <TableRow key={cfg.id}>
                      <TableCell>{formatMonth(cfg.effective_month)}</TableCell>
                      <TableCell><Badge variant="outline">{cfg.first_year_claimable_months}</Badge></TableCell>
                      <TableCell><Badge variant="outline">{cfg.renewal_year_claimable_months}</Badge></TableCell>
                      <TableCell>
                        <Badge className={cfg.include_one_time_charge ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                          {cfg.include_one_time_charge ? '100%' : 'No'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {cfg.is_locked ? (
                          <Badge className="bg-amber-100 text-amber-700"><Lock className="w-3 h-3 mr-1" />Locked</Badge>
                        ) : (
                          <Badge variant="outline" className="text-emerald-600"><LockOpen className="w-3 h-3 mr-1" />Active</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {!cfg.is_locked && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleLockConversionConfig(cfg.id)} title="Lock">
                                <Lock className="w-4 h-4 text-amber-500" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteConversionConfig(cfg.id)} title="Delete">
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                        No conversion config. Using defaults.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team SQD Incentive */}
        <TabsContent value="team-sqd" className="mt-6">
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-emerald-600" />
                  Team SQD Incentive Slabs
                </CardTitle>
                <CardDescription>Slab-based amount paid to employee per SQD</CardDescription>
              </div>
              <Button onClick={() => setIsAddTeamSqdSlabOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" /> Add Slab
              </Button>
            </CardHeader>
            <CardContent>
              <Alert className="mb-4">
                <Info className="w-4 h-4" />
                <AlertDescription>
                  Non-cumulative: The slab where employee&apos;s total SQD count falls applies to ALL their SQDs.
                </AlertDescription>
              </Alert>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[150px]">W.E.F.</TableHead>
                    <TableHead className="w-[120px]">SQD Range</TableHead>
                    <TableHead className="w-[150px]">Amount/SQD</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teamSqdSlabs.length > 0 ? teamSqdSlabs.map(slab => (
                    <TableRow key={slab.id}>
                      <TableCell>{formatMonth(slab.effective_month)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{slab.min_sqd} - {slab.max_sqd || '∞'}</Badge>
                      </TableCell>
                      <TableCell className="font-bold text-emerald-600">{formatCurrency(slab.amount_per_sqd)}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteTeamSqdSlab(slab.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                        No team SQD slabs configured. Using default ₹500/SQD.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Conversion Incentive */}
        <TabsContent value="team-conversion" className="mt-6">
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Percent className="w-5 h-5 text-emerald-600" />
                  Team Conversion Incentive
                </CardTitle>
                <CardDescription>Percentage paid to employee on deal conversion</CardDescription>
              </div>
              <Button onClick={() => setIsAddTeamConversionOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" /> Add Config
              </Button>
            </CardHeader>
            <CardContent>
              <Alert className="mb-4">
                <Info className="w-4 h-4" />
                <AlertDescription>
                  Calculation: (1st month plan × %) + (setup fee × %) + (renewal × %)
                </AlertDescription>
              </Alert>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[130px]">W.E.F.</TableHead>
                    <TableHead className="w-[130px]">1st Month %</TableHead>
                    <TableHead className="w-[130px]">Setup Fee %</TableHead>
                    <TableHead className="w-[130px]">Renewal %</TableHead>
                    <TableHead className="w-[100px]">Status</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teamConversionConfigs.length > 0 ? teamConversionConfigs.map(cfg => (
                    <TableRow key={cfg.id}>
                      <TableCell>{formatMonth(cfg.effective_month)}</TableCell>
                      <TableCell><Badge className="bg-blue-100 text-blue-700">{cfg.first_month_plan_percentage || 0}%</Badge></TableCell>
                      <TableCell><Badge className="bg-purple-100 text-purple-700">{cfg.setup_fee_percentage || 0}%</Badge></TableCell>
                      <TableCell><Badge className="bg-emerald-100 text-emerald-700">{cfg.renewal_first_month_percentage || 0}%</Badge></TableCell>
                      <TableCell>
                        {cfg.is_locked ? (
                          <Badge className="bg-amber-100 text-amber-700"><Lock className="w-3 h-3 mr-1" />Locked</Badge>
                        ) : (
                          <Badge variant="outline" className="text-emerald-600"><LockOpen className="w-3 h-3 mr-1" />Active</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {!cfg.is_locked && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleLockTeamConversionConfig(cfg.id)} title="Lock">
                                <Lock className="w-4 h-4 text-amber-500" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteTeamConversionConfig(cfg.id)} title="Delete">
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                        No team conversion config. Add to enable employee incentives on deals.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Company SQD Slab Modal */}
      <Dialog open={isAddCompanySqdOpen} onOpenChange={setIsAddCompanySqdOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add SQD Revenue Slab</DialogTitle>
            <DialogDescription>Configure company earnings per SQD</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Effective From Month</Label>
              <Input
                type="month"
                value={newCompanySqdSlab.effective_month}
                onChange={(e) => setNewCompanySqdSlab({...newCompanySqdSlab, effective_month: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min SQD Count</Label>
                <Input
                  type="number"
                  value={newCompanySqdSlab.min_sqd}
                  onChange={(e) => setNewCompanySqdSlab({...newCompanySqdSlab, min_sqd: parseInt(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Max SQD Count (empty = unlimited)</Label>
                <Input
                  type="number"
                  value={newCompanySqdSlab.max_sqd}
                  placeholder="∞"
                  onChange={(e) => setNewCompanySqdSlab({...newCompanySqdSlab, max_sqd: e.target.value ? parseInt(e.target.value) : ''})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Amount per SQD (₹)</Label>
              <Input
                type="number"
                value={newCompanySqdSlab.amount_per_sqd}
                onChange={(e) => setNewCompanySqdSlab({...newCompanySqdSlab, amount_per_sqd: parseFloat(e.target.value)})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddCompanySqdOpen(false)}>Cancel</Button>
            <Button onClick={handleAddCompanySqdSlab}>Add Slab</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Team SQD Slab Modal */}
      <Dialog open={isAddTeamSqdSlabOpen} onOpenChange={setIsAddTeamSqdSlabOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team SQD Incentive Slab</DialogTitle>
            <DialogDescription>Slab-based amount paid to employee per SQD</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Effective From Month</Label>
              <Input
                type="month"
                value={newTeamSqdSlab.effective_month}
                onChange={(e) => setNewTeamSqdSlab({...newTeamSqdSlab, effective_month: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min SQD Count</Label>
                <Input
                  type="number"
                  value={newTeamSqdSlab.min_sqd}
                  onChange={(e) => setNewTeamSqdSlab({...newTeamSqdSlab, min_sqd: parseInt(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Max SQD Count (empty = unlimited)</Label>
                <Input
                  type="number"
                  value={newTeamSqdSlab.max_sqd}
                  placeholder="∞"
                  onChange={(e) => setNewTeamSqdSlab({...newTeamSqdSlab, max_sqd: e.target.value ? parseInt(e.target.value) : ''})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Amount per SQD (₹)</Label>
              <Input
                type="number"
                value={newTeamSqdSlab.amount_per_sqd}
                onChange={(e) => setNewTeamSqdSlab({...newTeamSqdSlab, amount_per_sqd: parseFloat(e.target.value)})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddTeamSqdSlabOpen(false)}>Cancel</Button>
            <Button onClick={handleAddTeamSqdSlab} className="bg-emerald-600 hover:bg-emerald-700">Add Slab</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Team Conversion Config Modal */}
      <Dialog open={isAddTeamConversionOpen} onOpenChange={setIsAddTeamConversionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Conversion Incentive</DialogTitle>
            <DialogDescription>Percentage paid to employee on deal conversion</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Effective From Month</Label>
              <Input
                type="month"
                value={newTeamConversionConfig.effective_month}
                onChange={(e) => setNewTeamConversionConfig({...newTeamConversionConfig, effective_month: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>1st Month Plan Amount %</Label>
              <Input
                type="number"
                value={newTeamConversionConfig.first_month_plan_percentage}
                onChange={(e) => setNewTeamConversionConfig({...newTeamConversionConfig, first_month_plan_percentage: parseFloat(e.target.value)})}
              />
            </div>
            <div className="space-y-2">
              <Label>One-time/Setup Fee %</Label>
              <Input
                type="number"
                value={newTeamConversionConfig.setup_fee_percentage}
                onChange={(e) => setNewTeamConversionConfig({...newTeamConversionConfig, setup_fee_percentage: parseFloat(e.target.value)})}
              />
            </div>
            <div className="space-y-2">
              <Label>Renewal (each year 1st month) %</Label>
              <Input
                type="number"
                value={newTeamConversionConfig.renewal_first_month_percentage}
                onChange={(e) => setNewTeamConversionConfig({...newTeamConversionConfig, renewal_first_month_percentage: parseFloat(e.target.value)})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddTeamConversionOpen(false)}>Cancel</Button>
            <Button onClick={handleAddTeamConversionConfig} className="bg-emerald-600 hover:bg-emerald-700">Add Config</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Company Conversion Config Modal */}
      <Dialog open={isAddConversionOpen} onOpenChange={setIsAddConversionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Company Conversion Revenue Config</DialogTitle>
            <DialogDescription>Configure which months of plan amount we claim from SaaS company</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Effective From Month</Label>
              <Input
                type="month"
                value={newConversionConfig.effective_month}
                onChange={(e) => setNewConversionConfig({...newConversionConfig, effective_month: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>First Year Claimable Months (comma separated, e.g., 1,3,5)</Label>
              <Input
                value={newConversionConfig.first_year_months}
                onChange={(e) => setNewConversionConfig({...newConversionConfig, first_year_months: e.target.value})}
                placeholder="1,3,5"
              />
            </div>
            <div className="space-y-2">
              <Label>Renewal Year Claimable Months (comma separated, e.g., 1,2)</Label>
              <Input
                value={newConversionConfig.renewal_months}
                onChange={(e) => setNewConversionConfig({...newConversionConfig, renewal_months: e.target.value})}
                placeholder="1,2"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={newConversionConfig.include_one_time}
                onCheckedChange={(checked) => setNewConversionConfig({...newConversionConfig, include_one_time: checked})}
              />
              <Label>Include 100% of One-time Setup Charge</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddConversionOpen(false)}>Cancel</Button>
            <Button onClick={handleAddConversionConfig}>Add Config</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IncentiveConfigPage;
